import { useEffect, useState } from 'react';

import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import {
  Box,
  IconButton,
  InputAdornment,
  InputBase,
  InputBaseProps,
  Theme,
} from '@mui/material';

import { useDebounce } from '../../hooks';
import { SearchIcon } from '../icons';
import { SearchModeSuggestions } from './SearchModeSuggestions';

interface SearchBarProps extends InputBaseProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  debounced?: boolean;
  debounceDelay?: number;
  value?: string;
  /** When true, renders the search-mode picker (general vs. exact) below the input.
   * The picker reads/writes the shared mode via useSearchMode. */
  showExactMatch?: boolean;
}

const customStyles = {
  searchBar: {
    transition: 'border-color 0.1s ease-in-out',
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    fontSize: (theme: Theme) => theme.typography.body1.fontSize,
    marginTop: '2vh',
    marginBottom: '2vh',
    '&:hover': {
      borderColor: '#3E8FFA',
    },
    '&:focus-within': {
      borderColor: '#3E8FFA',
    },
    '& > input': {
      margin: '5px 6px',
      padding: '0px',
      lineHeight: (theme: Theme) => theme.typography.body1.lineHeight,
      fontSize: (theme: Theme) => theme.typography.body1.fontSize,
      height: (theme: Theme) => theme.typography.body1.lineHeight,
    },
  },
  searchIcon: {
    width: '12px',
    height: '12px',
    margin: '0 0 0 14px',
  },
  clearIcon: {
    color: '#B8BABF', // Neutral-6
    '& :hover': {
      backgroundColor: 'transparent',
      color: '#46484D', // Neutral-9
    },
    marginRight: '4px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
};

export const SearchBar = ({
  onSearch,
  placeholder,
  debounced,
  debounceDelay,
  value,
  showExactMatch,
  ...props
}: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const handleDebouncedSearch = useDebounce(() => onSearch(query), debounceDelay ?? 300);

  useEffect(() => {
    // Clear the search bar when the parent value is empty
    if (value === '') {
      setQuery(value);
    }
  }, [value]);

  const input = (
    <InputBase
      {...props}
      fullWidth
      value={query}
      sx={{ ...customStyles.searchBar, ...props.sx }}
      placeholder={placeholder}
      startAdornment={
        <InputAdornment position='start'>
          <SearchIcon sx={customStyles.searchIcon} />
        </InputAdornment>
      }
      endAdornment={
        query && (
          <InputAdornment position='end'>
            <IconButton
              sx={customStyles.clearIcon}
              onClick={() => {
                setQuery('');
                onSearch('');
              }}
              size='small'
              disableRipple
            >
              <ClearRoundedIcon fontSize='small' />
            </IconButton>
          </InputAdornment>
        )
      }
      onFocus={() => setSuggestionsOpen(query.trim().length > 0)}
      onBlur={() => setSuggestionsOpen(false)}
      onChange={(event) => {
        setQuery(event.target.value);
        setSuggestionsOpen(event.target.value.trim().length > 0);
        debounced ? handleDebouncedSearch() : onSearch(event.target.value);
      }}
    />
  );

  if (!showExactMatch) {
    return input;
  }

  return (
    <Box sx={customStyles.wrapper}>
      {input}
      <SearchModeSuggestions
        query={query}
        open={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
      />
    </Box>
  );
};
